import { useCallback, useEffect, useRef, useState } from 'react';
import { IonApp, IonAlert, IonToast } from '@ionic/react';
import { Backdrop } from './components/Shell';
import { Home } from './screens/Home';
import { DriverSetup } from './screens/DriverSetup';
import { DriverActive } from './screens/DriverActive';
import { ActiveRides } from './screens/ActiveRides';
import { PassengerJoin } from './screens/PassengerJoin';
import { PassengerWait } from './screens/PassengerWait';
import { Places } from './screens/Places';
import { Contacts } from './screens/Contacts';
import { ConnectAccept } from './screens/ConnectAccept';
import { ServerSetup } from './screens/ServerSetup';
import { api, ApiError } from './lib/api';
import {
  addDriverSession,
  clearSession,
  loadDriverSessions,
  loadSession,
  removeDriverSession,
  saveSession,
  type DriverSessionEntry,
  type Session,
} from './lib/session';
import { startTracking, type Tracker } from './lib/tracking';
import { registerServiceWorker } from './lib/push';
import { updateAvailable } from './lib/version';
import { takeNotificationClick, type NotificationClick } from './lib/notificationClick';
import { BACK_PRIORITY, exitApp, useBackButton, useHardwareBackButton } from './lib/backbutton';
import { clearBadge } from './lib/badge';
import { isServerConfigured } from './lib/server';
import { rememberMapConfig } from './lib/map';
import type { Connection, Ride } from './lib/types';

type View =
  | 'home'
  | 'driver-setup'
  | 'driver-active'
  | 'active-rides'
  | 'passenger-join'
  | 'passenger-wait'
  | 'places'
  | 'contacts'
  | 'connect-accept';

/** Code aus dem geteilten Fahrt-Link, z. B. /?code=ABC123 */
function codeFromUrl(): string | undefined {
  const code = new URLSearchParams(window.location.search).get('code');
  return code ? code.toUpperCase() : undefined;
}

/** Code aus einem Kontakt-Einladungslink, z. B. /?connect=ABC123 */
function connectCodeFromUrl(): string | undefined {
  const code = new URLSearchParams(window.location.search).get('connect');
  return code ? code.toUpperCase() : undefined;
}

/**
 * rideId+token aus einer Kontakt-Push-Benachrichtigung, wenn die App dafür
 * frisch geöffnet wurde (der `notificationclick`-Handler in sw.js hängt sie
 * an die geöffnete URL an - das ist robuster als sich auf postMessage zu
 * verlassen, das nur ein bereits offenes Fenster erreicht).
 */
function sessionFromUrl(): { rideId: string; token: string } | null {
  const params = new URLSearchParams(window.location.search);
  const rideId = params.get('rideId');
  const token = params.get('token');
  return rideId && token ? { rideId, token } : null;
}

/**
 * Zu welcher Fahrt ein Tipp auf eine Benachrichtigung fuehrt. Mitfahrer-Pushes
 * bringen ihr Token mit; Fahrer-Pushes nicht - deren Token kennt dieses Geraet
 * aber aus der Liste seiner eigenen Fahrten.
 */
function sessionForClick(click: NotificationClick | null): { rideId: string; token: string } | null {
  if (!click) return null;
  if (click.token) return { rideId: click.rideId, token: click.token };
  const own = loadDriverSessions().find((entry) => entry.rideId === click.rideId);
  return own ? { rideId: own.rideId, token: own.token } : null;
}

export function App() {
  const [view, setView] = useState<View>('home');
  const [session, setSession] = useState<Session | null>(null);
  const [ride, setRide] = useState<Ride | null>(null);
  const [toast, setToast] = useState<{ message: string; color: string } | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [linkCode] = useState<string | undefined>(codeFromUrl);
  // Beim Start aus der Adresszeile, spaeter auch von Hand eingetippt (siehe
  // `Contacts.onEnterCode`). Ein getippter Code darf die App nicht ueber die
  // Adresszeile schicken: in der Android-App wuerde die WebView damit auf die
  // Server-Webseite springen und der Nutzer stuende ausserhalb der App.
  const [connectCode, setConnectCode] = useState<string | undefined>(connectCodeFromUrl);
  const [notifiedSession] = useState(sessionFromUrl);
  // Den beim Start abgelegten Tipp nur einmal abholen - auch wenn der
  // Einstiegs-Effekt (StrictMode, Serveradresse) mehrfach laeuft.
  const initialClick = useRef<Promise<NotificationClick | null> | null>(null);
  const [startContact, setStartContact] = useState<Connection | null>(null);
  // Fahrten, die dieses Geraet als Fahrer angelegt und noch nicht beendet
  // hat - unabhaengig davon, welche davon gerade angezeigt oder getrackt wird.
  const [activeRides, setActiveRides] = useState<DriverSessionEntry[]>(() => loadDriverSessions());
  // Die (immer nur eine) laufende Standortuebertragung lebt hier statt im
  // DriverActive-Screen, damit sie beim Wegnavigieren zu einer anderen
  // Fahrt nicht abreisst.
  const [tracked, setTracked] = useState<{ rideId: string; tracker: Tracker } | null>(null);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  // In der verpackten App zuerst die Serveradresse - ohne sie hat kein
  // einziger API-Aufruf eine Chance.
  const [setupServer, setSetupServer] = useState(() => !isServerConfigured());
  const [updateReady, setUpdateReady] = useState(false);

  const showError = useCallback((message: string) => setToast({ message, color: 'danger' }), []);
  const showNotice = useCallback((message: string) => setToast({ message, color: 'primary' }), []);

  const begin = useCallback((next: Session, fresh: Ride) => {
    saveSession(next);
    setSession(next);
    setRide(fresh);
    if (next.role === 'driver') setActiveRides(addDriverSession(next));
  }, []);

  const beginTracking = useCallback(async (): Promise<void> => {
    if (!session || session.role !== 'driver') return;
    if (tracked && tracked.rideId !== session.rideId) {
      throw new Error('Du überträgst schon eine andere Fahrt - beende sie zuerst.');
    }
    const targetSession = session;
    const handle = await startTracking(targetSession.rideId, targetSession.token, {
      onRide: (fresh) => {
        setTrackingError(null);
        setRide((current) => (current && current.id === fresh.id ? fresh : current));
      },
      onError: setTrackingError,
    });
    setTracked({ rideId: targetSession.rideId, tracker: handle });
    showNotice(
      handle.background
        ? 'Tracking läuft - auch bei ausgeschaltetem Display.'
        : 'Tracking läuft. Lass diese Seite offen und das Display an.',
    );
  }, [session, tracked, showNotice]);

  const endTracking = useCallback(async () => {
    if (!tracked) return;
    await tracked.tracker.stop();
    setTracked(null);
    setTrackingError(null);
  }, [tracked]);

  // Springt direkt in eine Fahrt, ohne Code-Eingabe - der Kurzweg für
  // Kontakte, deren Push rideId+token schon mitbringt. Der Mitfahrer landet
  // dabei direkt in der Wartesicht statt im Bestätigen-Bildschirm: bei einem
  // Kontakt ist schon Vertrauen da, ein Abholpunkt lässt sich dort weiterhin
  // jederzeit optional setzen oder anpassen (siehe `PickupEditor`).
  const openRideFromNotification = useCallback(
    async (rideId: string, token: string) => {
      try {
        const { ride: fresh } = await api.get(rideId, token);
        begin({ rideId, token, role: fresh.role }, fresh);
        setView(fresh.role === 'driver' ? 'driver-active' : 'passenger-wait');
      } catch (error) {
        showError(error instanceof Error ? error.message : 'Fahrt konnte nicht geöffnet werden.');
      }
    },
    [begin, showError],
  );

  useEffect(() => {
    void registerServiceWorker();
  }, []);

  // Gibt es eine neuere Web-Version, darauf hinweisen - iOS haelt die Seite
  // sonst wochenlang vor. Bei jedem Sichtbarwerden pruefen, Popup statt
  // Zwangs-Reload: mitten in einer Fahrt die App neu zu starten waere schlimmer
  // als ein alter Stand.
  useEffect(() => {
    const check = () => void updateAvailable().then((available) => setUpdateReady(available));
    check();
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, []);

  // Welche Kartenkacheln geladen werden, sagt der Server (siehe lib/map.ts).
  // Kein Grund, daran etwas aufzuhaengen: klappt es nicht, bleibt es bei der
  // zuletzt gemerkten Karte.
  useEffect(() => {
    if (setupServer) return;
    api
      .config()
      .then((config) => rememberMapConfig(config.map))
      .catch(() => undefined);
  }, [setupServer]);

  // Klick auf eine Benachrichtigung, während die App schon offen war - oder
  // im Hintergrund lag. Auf dem iPhone kommt die postMessage aus sw.js dabei
  // nicht verlässlich an, deshalb wird der dort abgelegte Tipp zusätzlich bei
  // jedem Sichtbarwerden abgeholt. `takeNotificationClick` liefert ihn nur
  // einmal, egal welcher der beiden Wege zuerst greift.
  useEffect(() => {
    if (setupServer) return;

    const handleClick = async (fallback: NotificationClick | null) => {
      const target = sessionForClick((await takeNotificationClick()) ?? fallback);
      if (target) void openRideFromNotification(target.rideId, target.token);
    };

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data?.kind !== 'notification-click') return;
      const fallback =
        typeof data.data?.rideId === 'string'
          ? { rideId: data.data.rideId, token: data.data.token ?? null, type: data.data.type ?? null }
          : null;
      void handleClick(fallback);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void handleClick(null);
    };

    navigator.serviceWorker?.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [setupServer, openRideFromNotification]);

  // Laufende Fahrt wiederherstellen - oder aus einer Benachrichtigung, einem
  // Kontakt-Link oder einem Fahrt-Link direkt einsteigen.
  useEffect(() => {
    // Solange die Serveradresse fehlt, ist jeder Wiederherstellungsversuch
    // sinnlos - erst einrichten, dann laden.
    if (setupServer) {
      setRestoring(false);
      return;
    }

    // Bewusst aus der Adresszeile statt aus dem Zustand: dieser Effekt
    // entscheidet nur den Einstieg beim Laden. Ein spaeter eingetippter Code
    // wuerde ihn sonst erneut ausloesen und eine laufende Fahrt wegdruecken.
    const urlConnectCode = connectCodeFromUrl();

    initialClick.current ??= takeNotificationClick();
    void initialClick.current.then((click) => {
      // Ein Tipp auf eine Benachrichtigung schlaegt die gespeicherte Sitzung:
      // er zeigt auf die Fahrt, um die es gerade geht. Frueher gewann hier
      // die alte Sitzung - war deren Fahrt schon abgelaufen, landete man auf
      // der Startseite und erst der zweite Tipp (bei offener App) half.
      const notified = sessionForClick(click) ?? notifiedSession;
      if (notified) {
        if (notifiedSession) window.history.replaceState({}, '', window.location.pathname);
        openRideFromNotification(notified.rideId, notified.token).finally(() => setRestoring(false));
        return;
      }

      const stored = loadSession();
      if (stored) {
        api
          .get(stored.rideId, stored.token)
          .then(({ ride: fresh }) => {
            if (fresh.status === 'cancelled') {
              clearSession();
              setView(linkCode ? 'passenger-join' : urlConnectCode ? 'connect-accept' : 'home');
              return;
            }
            setSession(stored);
            setRide(fresh);
            setView(stored.role === 'driver' ? 'driver-active' : 'passenger-wait');
          })
          .catch((error) => {
            // 403/404: die Fahrt gibt es nicht mehr. Alles andere ist ein Netzproblem,
            // dann behalten wir die Sitzung und versuchen es später erneut.
            if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
              clearSession();
            }
            if (linkCode) setView('passenger-join');
            else if (urlConnectCode) setView('connect-accept');
          })
          .finally(() => setRestoring(false));
        return;
      }

      if (urlConnectCode) {
        setView('connect-accept');
        setRestoring(false);
        return;
      }
      if (linkCode) setView('passenger-join');
      setRestoring(false);
    });
  }, [setupServer, linkCode, notifiedSession, openRideFromNotification]);

  const leave = useCallback(() => {
    if (session?.role === 'driver') {
      setActiveRides(removeDriverSession(session.rideId));
      if (tracked?.rideId === session.rideId) {
        void tracked.tracker.stop();
        setTracked(null);
        setTrackingError(null);
      }
    }
    clearSession();
    setSession(null);
    setRide(null);
    setStartContact(null);
    setView('home');
    // Query-Parameter aus der Adresszeile nehmen, sonst landet man beim
    // Neuladen wieder im selben Einstieg.
    window.history.replaceState({}, '', window.location.pathname);
    void clearBadge();
  }, [session, tracked]);

  // Die Zurücktaste soll denselben Weg gehen wie der Pfeil oben links -
  // deshalb hängt beides an dieser einen Stelle. `false` heisst: von hier
  // führt kein Weg mehr zurück, die App darf sich schliessen.
  const goBack = useCallback((): boolean => {
    if (setupServer) {
      // Beim allerersten Start führt kein Weg an der Serveradresse vorbei.
      if (!isServerConfigured()) return false;
      setSetupServer(false);
      return true;
    }
    if (restoring) return false;

    switch (view) {
      case 'places':
      case 'contacts':
      case 'active-rides':
        setView('home');
        return true;

      case 'connect-accept':
        // Wie in onDone: den Einladungscode aus der Adresszeile nehmen,
        // sonst steht er beim nächsten Start wieder da.
        window.history.replaceState({}, '', window.location.pathname);
        setConnectCode(undefined);
        setView('home');
        return true;

      case 'driver-setup': {
        const backTo = startContact ? 'contacts' : 'home';
        setStartContact(null);
        setView(backTo);
        return true;
      }

      case 'passenger-join':
        if (session) leave();
        else setView('home');
        return true;

      // Wegnavigieren beendet die Fahrt nicht mehr heimlich - eine laufende
      // Uebertragung lebt in `App.tsx` und laeuft unabhaengig vom Screen
      // weiter (siehe `onBeginTracking`). Gibt es mehrere laufende Fahrten,
      // geht's zur Liste zurueck, sonst nach Hause.
      case 'driver-active':
        setView(activeRides.length > 1 ? 'active-rides' : 'home');
        return true;

      // Startseite und wartender Mitfahrer sind Endstationen.
      case 'home':
      case 'passenger-wait':
        return false;
    }
  }, [setupServer, restoring, view, startContact, session, activeRides, leave]);

  const back = useCallback(() => {
    goBack();
  }, [goBack]);

  useHardwareBackButton();
  useBackButton(BACK_PRIORITY.view, (next) => {
    if (!goBack()) next();
  });
  useBackButton(BACK_PRIORITY.exit, () => exitApp());

  if (setupServer) {
    return (
      <IonApp>
        <Backdrop />
        <ServerSetup
          onDone={() => {
            setSetupServer(false);
            setRestoring(true);
          }}
          // Nur beim nachträglichen Ändern gibt es einen Weg zurück - beim
          // allerersten Start führt kein Weg an der Adresse vorbei.
          onCancel={isServerConfigured() ? back : undefined}
        />
      </IonApp>
    );
  }

  if (restoring) {
    return (
      <IonApp>
        <Backdrop />
      </IonApp>
    );
  }

  return (
    <IonApp>
      <Backdrop />

      {view === 'home' && (
        <Home
          onDrive={() => {
            setStartContact(null);
            setView('driver-setup');
          }}
          onRide={() => setView('passenger-join')}
          onPlaces={() => setView('places')}
          onContacts={() => setView('contacts')}
          onStartRideWithContact={(contact) => {
            setStartContact(contact);
            setView('driver-setup');
          }}
          onServerSettings={() => setSetupServer(true)}
          activeRideCount={activeRides.length}
          onActiveRides={() => setView('active-rides')}
        />
      )}

      {view === 'places' && <Places onBack={back} />}

      {view === 'active-rides' && (
        <ActiveRides
          trackedRideId={tracked?.rideId ?? null}
          onBack={back}
          onOpen={(next, fresh) => {
            begin(next, fresh);
            setView('driver-active');
          }}
        />
      )}

      {view === 'contacts' && (
        <Contacts
          onBack={back}
          onError={showError}
          onNotice={showNotice}
          onStartRide={(contact) => {
            setStartContact(contact);
            setView('driver-setup');
          }}
          onEnterCode={(code) => {
            setConnectCode(code);
            setView('connect-accept');
          }}
        />
      )}

      {view === 'connect-accept' && connectCode && (
        <ConnectAccept
          code={connectCode}
          onError={showError}
          onDone={back}
        />
      )}

      {view === 'driver-setup' && (
        <DriverSetup
          contact={startContact}
          onBack={back}
          onError={showError}
          onNotice={showNotice}
          onCreated={(next, fresh) => {
            begin(next, fresh);
            setView('driver-active');
          }}
        />
      )}

      {view === 'driver-active' && session && ride && (
        <DriverActive
          session={session}
          ride={ride}
          contact={startContact}
          onBack={() => setView(activeRides.length > 1 ? 'active-rides' : 'home')}
          isTracking={tracked?.rideId === session.rideId}
          otherRideTracking={tracked !== null && tracked.rideId !== session.rideId}
          trackingError={tracked?.rideId === session.rideId ? trackingError : null}
          onBeginTracking={beginTracking}
          onEndTracking={endTracking}
          onRide={setRide}
          onFinished={leave}
          onError={showError}
          onNotice={showNotice}
        />
      )}

      {view === 'passenger-join' && (
        <PassengerJoin
          initialCode={linkCode}
          session={session}
          ride={ride}
          onBack={back}
          onError={showError}
          onNotice={showNotice}
          onJoined={begin}
          onReady={() => setView('passenger-wait')}
        />
      )}

      {view === 'passenger-wait' && session && ride && (
        <PassengerWait
          session={session}
          ride={ride}
          onRide={setRide}
          onLeave={leave}
          onError={showError}
        />
      )}

      <IonToast
        isOpen={toast !== null}
        message={toast?.message}
        color={toast?.color}
        duration={4000}
        position="top"
        onDidDismiss={() => setToast(null)}
      />
      <IonAlert
        isOpen={updateReady}
        header="Neue Version verfügbar"
        message="Es gibt eine neuere Version der App. Jetzt neu laden?"
        buttons={[
          { text: 'Später', role: 'cancel', handler: () => setUpdateReady(false) },
          { text: 'Neu laden', handler: () => window.location.reload() },
        ]}
      />
    </IonApp>
  );
}
